import { useState } from "react";
import { X } from "lucide-react";
import { compressImage } from "./constants";

/**
 * Modal dialog for swapping / reordering / adding photos on an existing
 * listing without opening the full wizard. Compresses on upload (same
 * as the wizard) and calls onSave with the new image array.
 */
export default function PhotoSwapDialog({ product, onClose, onSave }) {
    const [images, setImages] = useState(Array.isArray(product.images) ? product.images : []);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");
    const [dragIdx, setDragIdx] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);

    const onFiles = async (files) => {
        setErr("");
        const list = Array.from(files);
        for (const f of list) {
            if (!f.type.startsWith("image/")) { setErr("Only image files are supported."); return; }
            if (f.size > 15 * 1024 * 1024) { setErr(`${f.name} is over 15MB.`); return; }
        }
        setUploading(true);
        try {
            const remaining = 8 - images.length;
            if (remaining <= 0) { setErr("Maximum 8 photos per listing — remove one first."); return; }
            const compressed = [];
            for (const f of list.slice(0, remaining)) {
                compressed.push(await compressImage(f));
            }
            setImages((prev) => [...prev, ...compressed].slice(0, 8));
        } catch {
            setErr("Failed to process one of the images.");
        } finally {
            setUploading(false);
        }
    };

    const remove = (i) => setImages((prev) => prev.filter((_, x) => x !== i));
    const move = (from, to) => {
        if (from === to || to < 0 || to >= images.length) return;
        setImages((prev) => {
            const arr = [...prev];
            const [x] = arr.splice(from, 1);
            arr.splice(to, 0, x);
            return arr;
        });
    };

    const replaceAll = () => {
        if (!window.confirm("Remove all current photos and start fresh?")) return;
        setImages([]);
    };

    const save = async () => {
        setSaving(true);
        try {
            await onSave(images);
            onClose();
        } catch (e) {
            setErr(e?.response?.data?.detail || "Couldn't save photos. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            data-testid="photo-swap-dialog"
            onClick={onClose}
            style={{
                position: "fixed", inset: 0, background: "rgba(28,12,6,0.72)",
                display: "flex", alignItems: "center", justifyContent: "center",
                zIndex: 1000, padding: 20,
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    background: "var(--parchment)", maxWidth: 720, width: "100%",
                    maxHeight: "90vh", overflow: "auto",
                    border: "1px solid var(--sand-line)", padding: 28,
                }}
            >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
                    <div>
                        <div className="eyebrow">Swap photos</div>
                        <h3 style={{ fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: "1.4rem", color: "var(--cocoa)", margin: "4px 0 0" }}>
                            {product.title}
                        </h3>
                        <div style={{ fontFamily: "var(--font-serif)", color: "var(--cocoa)", opacity: 0.7, fontSize: 13, marginTop: 4 }}>
                            {product.designer} · SKU {product.sku || "—"}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        data-testid="photo-swap-close"
                        style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--cocoa)" }}
                    >
                        <X size={20} strokeWidth={1.4} />
                    </button>
                </div>

                <label
                    className="upload-dropzone"
                    data-testid="photo-swap-drop"
                    onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                    onDragLeave={() => setIsDragOver(false)}
                    onDrop={(e) => {
                        e.preventDefault(); setIsDragOver(false);
                        if (e.dataTransfer.files.length) onFiles(e.dataTransfer.files);
                    }}
                    style={{
                        marginBottom: images.length ? 16 : 0,
                        background: isDragOver ? "var(--parchment-soft)" : undefined,
                        borderColor: isDragOver ? "var(--cocoa)" : undefined,
                    }}
                >
                    {uploading
                        ? "Processing…"
                        : images.length >= 8
                            ? "Maximum 8 photos reached — remove one to add more"
                            : "Drop new photos here or click to choose — JPG · PNG · HEIC · max 15MB each"}
                    <input
                        type="file"
                        accept="image/*"
                        multiple
                        style={{ display: "none" }}
                        onChange={(e) => onFiles(e.target.files)}
                        disabled={images.length >= 8}
                        data-testid="photo-swap-file-input"
                    />
                </label>

                {err && <div className="form-error" data-testid="photo-swap-error" style={{ marginBottom: 12 }}>{err}</div>}

                {images.length > 0 && (
                    <>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
                            <div className="eyebrow">Current photos ({images.length}/8)</div>
                            <button
                                type="button"
                                onClick={replaceAll}
                                data-testid="photo-swap-clear"
                                style={{
                                    background: "transparent", border: "none", cursor: "pointer",
                                    color: "#7a2f22",
                                    fontFamily: "var(--font-sans)", fontSize: 10,
                                    letterSpacing: "0.24em", textTransform: "uppercase",
                                }}
                            >
                                Clear all
                            </button>
                        </div>
                        <div className="upload-previews">
                            {images.map((src, i) => (
                                <div
                                    key={i}
                                    className="upload-preview"
                                    data-testid={`photo-swap-thumb-${i}`}
                                    draggable
                                    onDragStart={() => setDragIdx(i)}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={() => { if (dragIdx !== null) move(dragIdx, i); setDragIdx(null); }}
                                    style={{ cursor: "grab", opacity: dragIdx === i ? 0.4 : 1 }}
                                >
                                    <img src={src} alt="" />
                                    {i === 0 && (
                                        <span style={{
                                            position: "absolute", top: 4, left: 4,
                                            background: "rgba(240,226,192,0.95)", fontSize: 9,
                                            padding: "2px 6px", letterSpacing: "0.2em", textTransform: "uppercase",
                                        }}>
                                            Primary
                                        </span>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => remove(i)}
                                        aria-label="Remove"
                                        data-testid={`photo-swap-remove-${i}`}
                                    >
                                        <X size={12} strokeWidth={1.6} />
                                    </button>
                                    <div style={{ position: "absolute", bottom: 4, left: 4, display: "flex", gap: 2 }}>
                                        <button
                                            type="button" onClick={() => move(i, i - 1)} disabled={i === 0}
                                            style={{
                                                background: "rgba(28,12,6,0.72)", color: "var(--ivory)",
                                                border: "none", width: 20, height: 20,
                                                cursor: i === 0 ? "not-allowed" : "pointer", fontSize: 11,
                                            }}
                                        >◀</button>
                                        <button
                                            type="button" onClick={() => move(i, i + 1)} disabled={i === images.length - 1}
                                            style={{
                                                background: "rgba(28,12,6,0.72)", color: "var(--ivory)",
                                                border: "none", width: 20, height: 20,
                                                cursor: i === images.length - 1 ? "not-allowed" : "pointer", fontSize: 11,
                                            }}
                                        >▶</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div style={{
                            marginTop: 8, fontFamily: "var(--font-serif)", fontStyle: "italic",
                            color: "var(--cocoa)", opacity: 0.7, fontSize: 13,
                        }}>
                            Drag to reorder · first is the primary photo shown in the shop
                        </div>
                    </>
                )}

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 24 }}>
                    <button
                        className="btn btn-outline"
                        onClick={onClose}
                        disabled={saving}
                        data-testid="photo-swap-cancel"
                    >
                        Cancel
                    </button>
                    <button
                        className="btn"
                        onClick={save}
                        disabled={saving}
                        data-testid="photo-swap-save"
                    >
                        {saving ? "Saving…" : "Save photos"}
                    </button>
                </div>
            </div>
        </div>
    );
}
